import { AxiosInstance } from 'axios';

export const authInterceptor = (httpClient: AxiosInstance, token: string | undefined) => {
  const interceptorId = httpClient.interceptors.request.use(
    config => {
      if (token) {
        config.headers.set('Authorization', `Bearer ${token}`);
      } else {
        console.warn('⚠️ No token available for request:', config.url);
      }
      return config;
    },
    error => Promise.reject(error),
  );
  return interceptorId;
};

/**
 * Attach a Bearer token to every outgoing request.
 *
 * The getter is `async` on purpose: callers (see `KeycloakProvider`) pass
 * `getToken` from `keycloak.config`, which awaits `keycloak.init()` and then
 * silently refreshes the token if it's about to expire. Doing this in the
 * interceptor — instead of reading `keycloak.token` synchronously — closes
 * the race where a request fires before `keycloak.init()` has resolved (or
 * after the token expired) and goes out without an Authorization header.
 *
 * Axios fully supports `async` request interceptors (the request promise
 * waits for the interceptor to resolve), so this does not block the rest of
 * the app — only the individual outgoing HTTP call.
 */
export const authInterceptorWithGetter = (
  httpClient: AxiosInstance,
  getToken: () => Promise<string | null | undefined> | string | null | undefined,
) => {
  return httpClient.interceptors.request.use(
    async config => {
      const token = await getToken();
      if (token) {
        config.headers.set('Authorization', `Bearer ${token}`);
      }
      return config;
    },
    error => Promise.reject(error),
  );
};
